import {I18nString} from "../../models/lang/i18nString";
import {LangKey} from "../../hooks/globalContext";
import {ITranslateResponse} from "../../models/api/translate";

/**
 * Performs one translation request. Supplied by the caller rather than imported
 * so this module stays free of the API client, which lives in React context and
 * cannot be reached from a plain module.
 */
export type TranslateFn = (text: string, from: string, to: string[]) => Promise<ITranslateResponse>;

/**
 * Fills every missing target language for one i18n field.
 *
 * onResult is called once per successfully translated language, with the whole
 * field so far. onError is called once per language that failed - the caller
 * MUST surface it.
 *
 * This never rejects. A field left silently unchanged is indistinguishable from
 * a successful translation, which is the bug this whole path exists to prevent,
 * so a transport failure becomes one onError per requested language instead.
 */
export async function I18nTranslate(
    sourceText: I18nString,
    from: LangKey | string,
    to: LangKey | string[],
    onResult: (result: I18nString) => void,
    onError: (lang: string, message: string) => void,
    translate: TranslateFn,
): Promise<void> {
    if (!sourceText) {
        return;
    }
    const source = (sourceText[from] || "").trim();
    if (!source) {
        return;
    }

    const targets = Array.from(new Set(Array.isArray(to) ? to : [to]))
        .filter((lang) => lang !== from)
        .filter((lang) => !sourceText[lang]);

    if (targets.length === 0) {
        return;
    }

    // One request covering every language. Issuing one per language is what got
    // the previous implementation rate-limited.
    let response: ITranslateResponse;
    try {
        response = await translate(source, from, targets);
    } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        for (const lang of targets) {
            onError(lang, message);
        }
        return;
    }

    const translations = response?.translations || {};
    const errors = response?.errors || {};

    for (const lang of targets) {
        const translated = (translations[lang] || "").trim();
        if (translated) {
            sourceText = {...sourceText, [lang]: translated};
            onResult(sourceText);
            continue;
        }
        onError(lang, errors[lang] || "сервер не вернул перевод для этого языка");
    }
}
